/**
 * Tab5Bescheinigungen – Mietbescheinigung (Wohngeld-Formular WG 10) erzeugen.
 *
 * Objekt → Vertrag wählen → alle bekannten Werte kommen aus der DB (Badge „DB"),
 * fehlende Angaben trägt der User ein — sie werden gespeichert (pro Vertrag bzw.
 * pro Objekt) und beim nächsten Mal vorbelegt. Export = ausfüllbares PDF im
 * Original-Layout.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../../api/config";
import SchreibenPanel from "./SchreibenPanel";
import UebergabeprotokollForm from "./UebergabeprotokollForm";
import "./Tab5Bescheinigungen.css";

const API = `${API_BASE}/mietverwaltung/api`;

// Alle Dokumente, die wir für einen Vertrag erzeugen (Welle 83: Tab „Dokumente“).
// art "formular" = amtliches AcroForm-PDF (bescheinigung.py), art "schreiben" = Brief/Protokoll aus Textbaustein (kuendigung.py)
const GRUPPEN = [
  { key: "bescheinigungen", titel: "Bescheinigungen", dokumente: [
    { key: "mietbescheinigung_wohngeld", label: "Mietbescheinigung Wohngeld", sub: "Anlage zum Wohngeldantrag (NDS WOHNG-001)", art: "formular" },
    { key: "mietbescheinigung", label: "Mietbescheinigung Sozialamt", sub: "SGB XII, amtliches Formular NDS", art: "formular" },
    { key: "wohnungsgeber", label: "Wohnungsgeberbescheinigung", sub: "§ 19 BMG, Ein- und Auszug", art: "formular" },
    { key: "sepa", label: "SEPA-Mandat", sub: "Grundsteuer/Finanzamt je Objekt", art: "formular" },
  ] },
  { key: "kuendigung", titel: "Kündigung & Auszug", dokumente: [
    { key: "kuendigung", label: "Kündigungsschreiben", sub: "Vermieter kündigt, mit Widerspruchshinweis", art: "schreiben",
      beschreibung: "Ordentliche Kündigung durch den Vermieter (§ 573 BGB). Braucht einen Kündigungsvorgang „Vermieter kündigt“ — Frist, Grund und Widerspruchsfrist kommen aus dem Vorgang. Eigenhändig unterschreiben und nachweisbar zustellen." },
    { key: "bestaetigung", label: "Kündigungsbestätigung", sub: "Mieter hat gekündigt", art: "schreiben",
      beschreibung: "Bestätigt dem Mieter den Zugang seiner Kündigung und das Vertragsende, mit Hinweisen zu Übergabe, Zählerständen, Kaution und Ummeldung. Braucht einen Kündigungsvorgang „Mieter kündigt“." },
    { key: "kautionsabrechnung", label: "Kautionsabrechnung", sub: "nach Auszug und Prüfung", art: "schreiben",
      beschreibung: "Abrechnung der Mietkaution mit Zinsen, Abzügen und Einbehalt für die Nebenkostenabrechnung. Beträge im Text eintragen." },
  ] },
  // Welle 109.1: Übergabe gibt es beim Einzug UND beim Auszug — ohne Kündigung
  { key: "uebergabe", titel: "Übergabe", dokumente: [
    { key: "uebergabeprotokoll", label: "Übergabeprotokoll", sub: "Einzug oder Auszug, mit Zählern und Räumen", art: "protokoll" },
  ] },
  // Welle 114: schnelle Rechnung/Gutschrift — Objekt oder Firma an Mieter, Objekt, Firma, Kreditor (alles aus DB 2)
  { key: "rechnungen", titel: "Rechnungen", dokumente: [
    { key: "rechnung", label: "Rechnung / Gutschrift", sub: "jetzt unter „Rechnungen“ (Welle 119)", art: "rechnung" },
  ] },
];
const DOKUMENTE = GRUPPEN.flatMap((g) => g.dokumente);

// SEPA-Mandat hängt am OBJEKT (Grundsteuer/Finanzamt) — kein Mietvertrag nötig.
// Die Maske zeigt je nach Bundesland/Formular die passenden Felder + Steuerarten.
function sepaSektionen(bl) {
  const kontoinhaber = { titel: "Kontoinhaber", keys: [
    "sepa_name", "sepa_strasse", "sepa_plz", "sepa_ort", "sepa_land", "sepa_abw_name"] };
  const bank = { titel: "Bankverbindung", keys: ["sepa_iban", "sepa_bic", "sepa_bank"] };
  const grundstueck = { titel: "Grundstück (bei Grundsteuer)", keys: [
    "sepa_objekt_strasse", "sepa_objekt_ort"] };
  const unterschrift = { titel: "Unterschrift", keys: ["sepa_ort_unterschrift", "sepa_datum"] };

  if (bl === "NDS_grundsteuer") {
    return [
      { titel: "Formular", keys: ["sepa_bundesland"] },
      { titel: "Gemeinde-Grundsteuer (Kommunalformular)", keys: [
        "sepa_gs_behoerde", "sepa_gs_glaeubiger_id", "sepa_gs_mandatsref",
        "sepa_gs_kassenzeichen", "sepa_gs_grundsteuer_b", "sepa_gs_grundsteuer_a"] },
      kontoinhaber, bank, grundstueck, unterschrift,
    ];
  }
  if (bl === "NDS_schneverdingen") {  // Welle 135: Stadtkasse Schneverdingen – Grundsteuer Standard, Rest von Hand
    return [
      { titel: "Formular", keys: ["sepa_bundesland"] },
      { titel: "Stadtkasse Schneverdingen", keys: ["sepa_gs_kassenzeichen", "sepa_gs_glaeubiger_id"] },
      { titel: "Steuerart", keys: [
        "sepa_sv_grundsteuer", "sepa_objekt_strasse", "sepa_objekt_ort", "sepa_aktenzeichen",
        "sepa_sv_gewerbesteuer", "sepa_sv_gewerbe_az", "sepa_sv_hundesteuer",
        "sepa_sv_sonstiges", "sepa_sv_sonstiges_text"] },
      { ...kontoinhaber, keys: ["sepa_name", "sepa_strasse", "sepa_plz", "sepa_ort", "sepa_telefon"] },
      bank, unterschrift,
    ];
  }
  if (bl === "NDS") {  // Niedersachsen – Finanzamt-Mandat (ESt/USt …)
    return [
      { titel: "Formular & Finanzamt", keys: [
        "sepa_bundesland", "sepa_finanzamt", "sepa_stnr", "sepa_gilt_alle"] },
      { titel: "Steuerarten (wenn nicht für alle Steuern)", keys: [
        "sepa_art_est", "sepa_art_est_ohne", "sepa_art_ust", "sepa_art_ust_ohne",
        "sepa_art_lst", "sepa_art_kapest50a", "sepa_art_bauabzug", "sepa_art_kapest_schuldner"] },
      kontoinhaber, bank, unterschrift,
    ];
  }
  if (bl === "HH") {  // Hamburg: FA für Verkehrsteuern und Grundbesitz (fest im Kopf)
    return [
      { titel: "Formular & Steuernummer", keys: ["sepa_bundesland", "sepa_stnr"] },
      { titel: "Steuerarten (Hamburg — ankreuzen)", keys: [
        "sepa_hh_grundsteuer", "sepa_hh_hundesteuer", "sepa_hh_zweitwohnung",
        "sepa_hh_kultur_tourismus", "sepa_hh_spielvergnuegung", "sepa_hh_erbschaft_schenkung"] },
      kontoinhaber, bank, grundstueck, unterschrift,
    ];
  }
  // Bremen (Grundbesitzabgaben / allgemein)
  return [
    { titel: "Formular & Finanzamt", keys: [
      "sepa_bundesland", "sepa_finanzamt", "sepa_stnr", "sepa_gilt_alle"] },
    kontoinhaber, bank, grundstueck, unterschrift,
  ];
}

// Gliederung der Wohnungsgeberbescheinigung
const WG_SEKTIONEN = [
  { titel: "Wohnung & Einzug", keys: [
    "wg_wohnung_adresse", "wg_stockwerk_lage", "wg_eingezogen_am",
  ]},
  { titel: "Eingezogene Personen", keys: [
    "wg_person_1", "wg_person_2", "wg_person_3", "wg_person_4", "wg_person_5",
    "wg_weitere_beiblatt",
  ]},
  { titel: "Wohnungsgeber & Eigentümer", keys: [
    "wg_geber_name", "wg_geber_anschrift", "wg_beauftragte_person",
    "wg_ist_eigentuemer_ja", "wg_ist_eigentuemer_nein",
    "wg_eigentuemer_name", "wg_eigentuemer_anschrift", "wg_ort_datum",
  ]},
];

// Welle 134: Mietbescheinigung Wohngeld — Abschnitte 1–12 wie auf dem Formular WOHNG-001
const MW_SEKTIONEN = [
  { titel: "Antrag", keys: ["mw_mietzuschuss", "mw_lastenzuschuss"] },
  { titel: "1 Vermieter", keys: ["mw_verm_name", "mw_verm_vorname", "mw_verm_anschrift", "mw_verm_telefon"] },
  { titel: "2–4 Wohnung, Mieter/in, Vertrag", keys: [
    "mw_wohnung_anschrift", "mw_wohnung_telefon",
    "mw_mieter_name", "mw_mieter_vorname", "mw_mieter_geburtsdatum", "mw_mieter_telefon",
    "mw_vp1_name", "mw_vp1_vorname", "mw_vp2_name", "mw_vp2_vorname", "mw_mietbeginn",
  ]},
  { titel: "5–7 Fläche, Förderung, Garage", keys: [
    "mw_flaeche", "mw_unterverm", "mw_unterverm_qm", "mw_gewerbe", "mw_gewerbe_qm",
    "mw_gefoerdert_ja", "mw_garage_ja", "mw_anz_garagen", "mw_anz_stellpl",
  ]},
  { titel: "8–9 Miete und enthaltene Kosten", keys: [
    "mw_miete", "mw_kabel", "mw_kabel_betrag", "mw_heizung", "mw_heizung_betrag",
    "mw_warmwasser", "mw_warmwasser_betrag", "mw_strom", "mw_strom_betrag",
    "mw_haushalt", "mw_haushalt_betrag", "mw_garage_kosten", "mw_garage_betrag",
    "mw_sonst", "mw_sonst_text", "mw_sonst_betrag",
  ]},
  { titel: "10–12 Zahlung, Minderung, Mietschulden", keys: [
    "mw_zahlweise", "mw_minderung_ja", "mw_urteil_ja", "mw_minderung_seit", "mw_minderung_betrag",
    "mw_schulden_betrag", "mw_schulden_zeitraum", "mw_bemerkungen", "mw_ort_datum",
  ]},
];

// Gliederung der Mietbescheinigung (Reihenfolge wie auf dem Formular)
const SEKTIONEN = [
  { titel: "Mieter & Wohnung", keys: [
    "wohngeldnummer", "mieter_name", "haus_adresse",
    "lage_eg", "lage_og", "lage_og_nr", "lage_dg", "lage_rechts", "lage_mitte", "lage_links",
    "als_hauptmieter", "als_untermieter", "als_sonstige", "als_wohnbesitz",
    "bezogen_am", "anzahl_personen", "mitbewohner_1", "mitbewohner_2",
  ]},
  { titel: "Angaben über die Wohnung", keys: [
    "gefoerdert_ja", "zimmer", "kueche", "bad", "gesamtflaeche",
    "sammelheizung_ja", "fernheizung_ja", "ofenheizung_ja", "brennstoff",
    "gewerbe_ja", "gewerbe_qm", "untervermietet_qm",
    "erstbezug_jahr", "umbau_ja", "umbau_jahr",
    "bescheid_am_chk", "bescheid_am", "bescheid_nicht",
  ]},
  { titel: "Miete", keys: [
    "miete_seit", "miete_monatlich", "miete_abw_seit", "miete_abw_monat",
  ]},
  { titel: "Umlagen (in der Gesamtmiete enthalten)", keys: [
    "uml_a_ja", "uml_a_betrag", "uml_b_ja", "uml_b_betrag",
    "uml_c_ja", "uml_c_betrag", "uml_d_ja", "uml_d_betrag",
    "uml_e_garage", "uml_e_pkw", "uml_e_ja", "uml_e_betrag",
  ]},
  { titel: "Zuschläge / Vergütungen (Seite 2)", keys: [
    "zus_f_ja", "zus_f_betrag", "zus_g_ja", "zus_g_betrag",
    "zus_h_ja", "zus_h_betrag", "zus_i_voll", "zus_i_teil", "zus_i_ja", "zus_i_betrag",
    "zus_j_ja", "zus_j_betrag", "zus_k_ja", "zus_k_betrag",
    "zus_l_ja", "zus_l_betrag", "zus_m_text", "zus_m_ja", "zus_m_betrag",
    "verwandt_einem",
  ]},
  { titel: "Vermieter", keys: [
    "vermieter_name", "vermieter_anschrift", "vermieter_telefon", "ort_datum",
  ]},
];

// Welle 111: Formulare, deren Vorbelegung aus DB 2 kommt (Schalter „Daten aus" zum Vergleich mit DB 1)
const FORMULARE_DB2 = ["mietbescheinigung", "mietbescheinigung_wohngeld", "wohnungsgeber", "sepa"];

export default function Tab5Bescheinigungen({ initialVertrag = "", initialDoc = "" }) {
  const navigate = useNavigate();
  const [vertraege, setVertraege] = useState([]);
  const [objekt, setObjekt] = useState(initialVertrag ? initialVertrag.split("-")[0] : "");
  const [vertrag, setVertrag] = useState(initialVertrag);
  const [doc, setDoc] = useState(
    DOKUMENTE.some((d) => d.key === initialDoc) ? initialDoc : "mietbescheinigung");
  const [daten, setDaten] = useState(null);   // Antwort von /vorbelegung
  const [werte, setWerte] = useState({});
  const [dbWerte, setDbWerte] = useState({});   // Originalwerte aus den Stammdaten (für „↺ zurück")
  const [laden, setLaden] = useState(false);
  const [meldung, setMeldung] = useState(null);
  const [busy, setBusy] = useState(false);
  // Welle 110: Mietbescheinigung standardmäßig aus DB 2 (Laschen der Daten.xlsm), DB 1 nur zum Vergleich
  const [datenQuelle, setDatenQuelle] = useState("db2");

  // SEPA-Mandat referenziert das OBJEKT, die übrigen Dokumente den Mietvertrag.
  const istSepa = doc === "sepa";
  // SEPA: Bezug ist das Objekt — hat es mehrere Grundsteuerbescheide (Lasche „Grundsteuer": 176-01,
  // 176-02 …), wird je Bescheid ein Mandat erstellt; "" = erster Bescheid des Objekts
  const [gsWahl, setGsWahl] = useState("");
  const refKey = istSepa ? (gsWahl || objekt) : vertrag;
  const dokDef = DOKUMENTE.find((d) => d.key === doc) || DOKUMENTE[0];
  const istSchreiben = dokDef.art === "schreiben";
  const istProtokoll = dokDef.art === "protokoll";
  const istRechnung = dokDef.art === "rechnung";
  const [protokollArt, setProtokollArt] = useState(null);   // "einzug" | "auszug" → Formular offen

  useEffect(() => {
    fetch(`${API}/vertraege`).then((r) => r.json())
      .then((d) => setVertraege(d.vertraege || []))
      .catch(() => {});
  }, []);

  const objekte = useMemo(
    () => [...new Set(vertraege.map((v) => String(v.mietvertrag_nr || "").split("-")[0]))].sort(),
    [vertraege]);
  const vertraegeImObjekt = useMemo(
    () => vertraege.filter((v) => String(v.mietvertrag_nr || "").startsWith(objekt + "-")),
    [vertraege, objekt]);

  // behalten: lokale Änderungen an DB-Feldern, die das Neuladen überleben sollen
  const ladeVorbelegung = useCallback(async (nr, dokument, behalten = null, quelle = "db2") => {
    if (!nr) return;
    setLaden(true); setMeldung(null);
    try {
      const r = await fetch(`${API}/bescheinigung/vorbelegung?mietvertrag_nr=${encodeURIComponent(nr)}`
        + `&dokument=${encodeURIComponent(dokument)}`
        + (FORMULARE_DB2.includes(dokument) ? `&quelle=${quelle}` : ""));
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Laden fehlgeschlagen");
      const d = await r.json();
      setDaten(d);
      const neu = Object.fromEntries(Object.entries(d.felder).map(([k, v]) => [k, v.wert]));
      setDbWerte(Object.fromEntries(Object.entries(d.felder)
        .filter(([, v]) => v.quelle === "db").map(([k, v]) => [k, v.wert])));
      setWerte(behalten ? { ...neu, ...behalten } : neu);
    } catch (e) {
      setMeldung({ ok: false, text: String(e.message || e) });
      setDaten(null);
    } finally {
      setLaden(false);
    }
  }, []);

  useEffect(() => {
    if (refKey && !istSchreiben && !istProtokoll && !istRechnung) ladeVorbelegung(refKey, doc, null, datenQuelle); else setDaten(null);
  }, [refKey, doc, ladeVorbelegung, istSchreiben, istProtokoll, istRechnung, datenQuelle]);

  const zeige = (ok, text) => { setMeldung({ ok, text }); setTimeout(() => setMeldung(null), 5000); };

  const speichern = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/bescheinigung/zusatz`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mietvertrag_nr: refKey, felder: werte, dokument: doc, quelle: datenQuelle }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Speichern fehlgeschlagen");
      zeige(true, `Gespeichert (${d.vertrag} Vertrags- + ${d.objekt} Objekt-Angaben).`);
      // Geänderte DB-Felder werden nicht gespeichert — beim Neuladen aber auch nicht verworfen
      const behalten = Object.fromEntries(Object.keys(dbWerte)
        .filter((k) => werte[k] !== dbWerte[k]).map((k) => [k, werte[k]]));
      ladeVorbelegung(refKey, doc, behalten, datenQuelle);
    } catch (e) { zeige(false, String(e.message || e)); }
    finally { setBusy(false); }
  };

  const [ablegen, setAblegen] = useState(true);   // Welle 82: PDF zusätzlich im Vertragsordner (Dropbox) ablegen
  const quelleParam = FORMULARE_DB2.includes(doc) ? `quelle=${datenQuelle}` : "";

  const pdfErzeugen = async () => {
    setBusy(true);
    try {
      const q = [ablegen && !istSepa ? "ablegen=1" : "", quelleParam].filter(Boolean);
      const r = await fetch(`${API}/bescheinigung/pdf${q.length ? "?" + q.join("&") : ""}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mietvertrag_nr: refKey, felder: werte, dokument: doc }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "PDF fehlgeschlagen");
      const blob = await r.blob();
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="([^"]+)"/);
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : `${DOKUMENTE.find((x) => x.key === doc)?.label || "Dokument"} - ${refKey}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      const pfad = r.headers.get("X-Ablage-Pfad");
      const quelle = r.headers.get("X-Ablage-Quelle");
      zeige(true, pfad
        ? `PDF erzeugt und abgelegt: ${decodeURIComponent(pfad)}${quelle === "fallback" ? " (Sammelordner — kein Vertragsordner gefunden)" : ""}`
        : "PDF erzeugt — Zusatzangaben wurden gespeichert.");
      if (pfad) dokumenteLadenRef.current?.(vertrag);   // Welle 106.1: Liste unten mitziehen
    } catch (e) { zeige(false, String(e.message || e)); }
    finally { setBusy(false); }
  };

  const setW = (k, v) => setWerte((w) => ({ ...w, [k]: v }));

  // ja/nein-Toggle: setzt das ja-Feld, das Partner-nein-Feld invers
  const paarPartner = useMemo(() => {
    const map = {};
    (daten?.ja_nein_paare || []).forEach(([ja, nein]) => { map[ja] = nein; });
    return map;
  }, [daten]);

  const renderFeld = (key) => {
    if (!daten) return null;
    const info = daten.felder[key];
    if (!info) return null;
    const typ = daten.typen[key];
    const label = daten.labels[key];
    const quelle = info.quelle;
    // Alle Felder sind änderbar. DB-Werte sind nur Vorbelegung: Änderungen gelten für
    // dieses Dokument und werden nicht in die Stammdaten zurückgeschrieben.
    const geaendert = quelle === "db" && key in dbWerte && werte[key] !== dbWerte[key];
    const badge = quelle === "db" ? (
      <>
        <span className="mb-badge db">DB</span>
        {geaendert && (
          <button type="button" className="mb-zurueck" title="Zurück auf den Wert aus der Datenbank"
            onClick={(e) => { e.preventDefault(); setW(key, dbWerte[key]); }}>↺ DB-Wert</button>
        )}
      </>
    ) : quelle === "gespeichert" ? <span className="mb-badge saved">gespeichert</span>
      : (daten.nicht_in_db2 || []).includes(key)
        ? <span className="mb-badge" title="Gibt es in DB 2 noch nicht">nicht in DB 2</span> : null;

    // Finanzamt: Dropdown je Bundesland (auswählen statt tippen)
    const faOpts = (key === "sepa_finanzamt" && daten.finanzamt_optionen)
      ? (daten.finanzamt_optionen[werte.sepa_bundesland] || []) : null;
    if (typ === "select" || (faOpts && faOpts.length)) {
      const opts = faOpts && faOpts.length ? faOpts
        : ((daten.optionen && daten.optionen[key]) || []);
      const vorhanden = opts.some(([v]) => v === (werte[key] ?? ""));
      return (
        <label key={key} className="mb-feld">
          <span className="mb-label">{label} {badge}</span>
          <select className="mb-input" value={werte[key] ?? ""}
            onChange={(e) => setW(key, e.target.value)}>
            {!vorhanden && <option value={werte[key] ?? ""}>{werte[key] || "— bitte wählen —"}</option>}
            {opts.map(([val, txt]) => <option key={val} value={val}>{txt}</option>)}
          </select>
        </label>
      );
    }

    if (typ === "checkbox") {
      const nein = paarPartner[key];
      return (
        <label key={key} className="mb-feld mb-check">
          <input type="checkbox" checked={!!werte[key]}
            onChange={(e) => {
              setW(key, e.target.checked);
              if (nein) setW(nein, !e.target.checked);
            }} />
          <span>{label.replace(/: ja$/, "")}</span>
          {badge}
        </label>
      );
    }
    return (
      <label key={key} className="mb-feld">
        <span className="mb-label">{label} {badge}</span>
        <input className={`mb-input${geaendert ? " mb-geaendert" : ""}`} value={werte[key] ?? ""}
          placeholder={typ === "euro" ? "0,00" : ""}
          onChange={(e) => setW(key, e.target.value)} />
      </label>
    );
  };

  // Welle 106.1: Was zu diesem Vertrag schon abgelegt ist — bisher nur im Vorgang sichtbar
  const [dokumente, setDokumente] = useState([]);
  const dokumenteLadenRef = React.useRef(null);
  const [dokBusy, setDokBusy] = useState(false);
  const dokumenteLaden = React.useCallback(async (nr) => {
    if (!nr) { setDokumente([]); return; }
    try {
      const r = await fetch(`${API_BASE}/mietverwaltung/api/vertrag/${encodeURIComponent(nr)}/dokumente`);
      const d = await r.json();
      setDokumente(d.dokumente || []);
    } catch (_) { setDokumente([]); }
  }, []);
  useEffect(() => { dokumenteLadenRef.current = dokumenteLaden; }, [dokumenteLaden]);
  useEffect(() => { dokumenteLaden(vertrag); }, [vertrag, dokumenteLaden]);

  const dokumentHochladen = async (datei, typ) => {
    if (!datei || !vertrag) return;
    setDokBusy(true);
    try {
      const fd = new FormData();
      fd.append("datei", datei);
      fd.append("typ", typ);
      const r = await fetch(`${API_BASE}/mietverwaltung/api/vertrag/${encodeURIComponent(vertrag)}/dokument`,
        { method: "POST", body: fd });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Hochladen fehlgeschlagen");
      setMeldung({ ok: true, text: `„${d.dateiname}" abgelegt${d.pfad ? ` (${d.pfad})` : ""}.` });
      dokumenteLaden(vertrag);
    } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
    setDokBusy(false);
  };

  const mietvertragErzeugen = async () => {
    if (!vertrag) return;
    setDokBusy(true);
    try {
      const r = await fetch(`${API_BASE}/mietverwaltung/api/vertrag/${encodeURIComponent(vertrag)}/mietvertrag?ablegen=1`,
        { method: "POST" });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || "Mietvertrag konnte nicht erzeugt werden");
      }
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setMeldung({ ok: true, text: "Mietvertrag erzeugt und abgelegt." });
      dokumenteLaden(vertrag);
    } catch (e) { setMeldung({ ok: false, text: String(e.message || e) }); }
    setDokBusy(false);
  };

  const sektionen = doc === "sepa" ? sepaSektionen(werte.sepa_bundesland || "HH")
    : doc === "wohnungsgeber" ? WG_SEKTIONEN : doc === "mietbescheinigung_wohngeld" ? MW_SEKTIONEN : SEKTIONEN;
  // Welle 134: Vorschlag aus dem Kontoabgleich (Zahlweise, Mietschulden) — nur auf Klick übernehmen
  const vorschlag = doc === "mietbescheinigung_wohngeld" ? (daten?.vorschlaege || {}) : {};
  const vorschlagKeys = Object.keys(vorschlag).filter((k) => k.startsWith("mw_"));
  const vorschlagOffen = vorschlagKeys.some((k) => (werte[k] ?? "") !== vorschlag[k]);

  return (
    <div className="mb-wrap">
      <div className="mb-kopf">
        <h2>Dokumente</h2>
        <div className="mb-auswahl">
          <select value={objekt} onChange={(e) => { setObjekt(e.target.value); setVertrag(""); setGsWahl(""); setDaten(null); }}>
            <option value="">-- Objekt --</option>
            {objekte.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <select value={vertrag} onChange={(e) => setVertrag(e.target.value)}
            disabled={!objekt || istSepa}
            title={istSepa ? "SEPA-Mandat bezieht sich auf das Objekt — kein Mietvertrag nötig" : undefined}>
            <option value="">{istSepa ? "— (nur Objekt) —" : "-- Mietvertrag --"}</option>
            {vertraegeImObjekt.map((v) => (
              <option key={v.mietvertrag_nr} value={v.mietvertrag_nr}>
                {v.mietvertrag_nr} · {v.mieter1_name || v.mv_kurzname || v.name || "?"}
                {v.status && v.status !== "aktiv" ? ` (${v.status})` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-dokliste">
        {GRUPPEN.map((g) => (
          <div key={g.key} className="mb-dokgruppe">
            <div className="mb-dokgruppe-titel">{g.titel}</div>
            <div className="mb-dokkarten">
              {g.dokumente.map((d) => (
                <button key={d.key} type="button" className={`mb-dokkarte${doc === d.key ? " aktiv" : ""}`}
                  onClick={() => { setDoc(d.key); setMeldung(null); }}>
                  <span className="mb-dokkarte-label">{d.label}</span>
                  <span className="mb-dokkarte-sub">{d.sub}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Welle 106.1: abgelegte Dokumente des Vertrags — mit Hochladen */}
      {vertrag && !istSepa && (
        <div className="mb-dokgruppe" style={{ marginTop: 14 }}>
          <div className="mb-dokgruppe-titel">
            Abgelegte Dokumente ({dokumente.length})
          </div>
          <div style={{ padding: "8px 12px" }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
              <button type="button" className="mb-btn" disabled={dokBusy} onClick={mietvertragErzeugen}>
                Mietvertrag erzeugen
              </button>
              <label style={{ fontSize: 13 }}>
                Datei ablegen:{" "}
                <input type="file" accept="application/pdf" disabled={dokBusy}
                  onChange={(e) => { dokumentHochladen(e.target.files?.[0], "mietvertrag"); e.target.value = ""; }} />
              </label>
              {dokBusy && <span style={{ fontSize: 12.5, opacity: 0.7 }}>arbeitet …</span>}
            </div>
            {dokumente.length === 0 ? (
              <p style={{ fontSize: 13, opacity: 0.7, margin: 0 }}>
                Noch nichts abgelegt. Erzeugte Bescheinigungen und Schreiben erscheinen hier automatisch.
              </p>
            ) : (
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                {dokumente.map((d) => (
                  <li key={d.id} style={{ marginBottom: 4 }}>
                    {d.richtung === "eingehend" ? "↓" : "↑"} {d.dateiname}
                    <small style={{ opacity: 0.65 }}>
                      {" · "}{d.typ}{d.erzeugt_am ? ` · ${String(d.erzeugt_am).slice(0, 10).split("-").reverse().join(".")}` : ""}
                      {d.dropbox_pfad ? " · in Dropbox" : ""}
                    </small>
                    {d.dropbox_pfad && (
                      <button type="button" className="mb-link" style={{ marginLeft: 8 }}
                        onClick={() => window.open(`${API_BASE}/mietverwaltung/api/kuendigung/dokument/${d.id}/download`, "_blank")}>
                        herunterladen
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {istSchreiben && (
        <SchreibenPanel key={`${doc}-${vertrag}`} mietvertragNr={vertrag} typ={doc} beschreibung={dokDef.beschreibung}
          vertragName={(vertraegeImObjekt.find((v) => v.mietvertrag_nr === vertrag) || {}).name} />
      )}

      {istProtokoll && (
        <div className="mb-hinweis" style={{ display: "block" }}>
          {!vertrag ? "Bitte Objekt und Vertrag wählen." : (
            <>
              <p style={{ margin: "0 0 10px" }}>
                Wohnungsübergabe-Protokoll im Layout der Vorlage. Aus der Datenbank vorbelegt: Adresse und Lage,
                alle Mieter, Übergabetermin (aus dem Einzug bzw. der Kündigung, sonst Vertragsbeginn bzw. -ende),
                Räume der Einheit, Zählernummern der Einheit mit letztem bekanntem Stand, Schlüssel
                (Einzug: was der Vormieter zurückgegeben hat · Auszug: was beim Einzug übergeben wurde), Ort und Datum.
                Das PDF trägt einen QR-Code — unterschrieben eingescannt wird es automatisch zugeordnet.
              </p>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button className="me-btn me-btn-primary" onClick={() => setProtokollArt("einzug")}>
                  Übergabeprotokoll — vor dem Einzug</button>
                <button className="me-btn me-btn-outline" onClick={() => setProtokollArt("auszug")}>
                  Übergabeprotokoll — vor dem Auszug</button>
              </div>
            </>
          )}
        </div>
      )}
      {istProtokoll && protokollArt && vertrag && (
        <UebergabeprotokollForm mietvertragNr={vertrag} art={protokollArt} kuendigungId={null}
          onClose={() => setProtokollArt(null)}
          onErzeugt={() => zeige(true, "Übergabeprotokoll erzeugt und im Vertragsordner abgelegt.")} />
      )}

      {istRechnung && (
        <div className="mb-sektion">
          <div className="mb-sektion-titel">Rechnungen sind umgezogen</div>
          <p className="rg-klein" style={{ marginTop: 0 }}>Rechnungen und Gutschriften schreiben, das Rechnungsausgangsbuch und
            Lexware liegen jetzt zusammen unter <b>Rechnungen</b>.</p>
          <div className="mb-buttons" style={{ justifyContent: "flex-start" }}>
            <button className="mb-btn primary" onClick={() => {
              const q = new URLSearchParams({ tab: "schreiben" });
              if (vertrag) q.set("mv", vertrag); else if (objekt) q.set("objekt", objekt);
              navigate(`/rechnungen?${q}`);
            }}>→ In Rechnungen öffnen{vertrag ? ` (${vertrag})` : objekt ? ` (Objekt ${objekt})` : ""}</button>
          </div>
        </div>
      )}

      {meldung && <div className={`mb-meldung ${meldung.ok ? "ok" : "fehler"}`}>{meldung.text}</div>}
      {laden && <div className="mb-laden">Lädt Vorbelegung…</div>}

      {!istSchreiben && !istProtokoll && !istRechnung && daten && !laden && (
        <>
          {FORMULARE_DB2.includes(doc) && (
            <div className="mb-hinweis" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <b>Daten aus:</b>
              <label><input type="radio" checked={datenQuelle === "db2"} onChange={() => setDatenQuelle("db2")} />
                {" "}DB 2 — Laschen der Daten.xlsm{doc === "sepa" ? " (Objekte, Banken, SteuerNr, Grundsteuer)"
                  : " (Mietverträge, Mieten, Objekte, Adressen)"}</label>
              <label><input type="radio" checked={datenQuelle === "db1"} onChange={() => setDatenQuelle("db1")} />
                {" "}DB 1 (alt, zum Vergleich)</label>
              {daten?.daten_quelle === "db1_rueckfall" && <span style={{ fontSize: 12, opacity: 0.8 }}>
                {doc === "sepa" ? "Objekt" : "Vertrag"} steht noch nicht in DB 2 — Werte aus DB 1.</span>}
            </div>
          )}
          {istSepa && (daten.grundsteuer_bescheide || []).length > 1 && (
            <div className="mb-hinweis" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <b>Grundsteuerbescheid:</b>
              <select value={daten.grundsteuer_gewaehlt || ""} style={{ minWidth: 460, maxWidth: "100%" }}
                onChange={(e) => {
                  const v = e.target.value;
                  const erster = daten.grundsteuer_bescheide[0]?.id;
                  setGsWahl(v === erster ? "" : v);
                }}>
                {daten.grundsteuer_bescheide.map((b) => <option key={b.id} value={b.id}>{b.text}</option>)}
              </select>
              <span style={{ fontSize: 12, opacity: 0.8 }}>
                {daten.grundsteuer_bescheide.length} Bescheide zu Objekt {objekt} — je Bescheid ein eigenes Mandat
                (Grundsteuernummer, Kassenzeichen, Grundstück).
              </span>
            </div>
          )}
          <div className="mb-hinweis">
            {istSepa ? (
              <>
                Werte mit <span className="mb-badge db">DB</span> kommen aus Objekt, verknüpftem
                Bankkonto und der dem Objekt zugeordneten Steuernummer. <b>Bundesland/Formular</b>,
                Finanzamt, Ort und Datum sind änderbar. Fehlt die <b>Steuernummer</b>, bitte in der
                Datenpflege (Tabelle <i>steuernummer</i>) das Feld <i>objekt_id</i> beim passenden
                Eintrag setzen — dann wird sie hier automatisch vorbelegt. Das PDF bleibt das
                amtliche Formular und ist weiter ausfüllbar.
              </>
            ) : (
              <>
                Werte mit <span className="mb-badge db">DB</span> sind aus Mietvertrag, Miete, Adressen
                und den Objekt-/Einheiten-Stammdaten vorbelegt. Du kannst sie hier für dieses Dokument
                ändern — das ändert die <b>Stammdaten nicht</b>; „↺ DB-Wert" stellt den Originalwert
                wieder her. Zusätzliche Angaben ohne DB werden beim Speichern/Export gemerkt;
                objektweite Angaben gelten für alle Verträge des Objekts.
              </>
            )}
          </div>
          {vorschlag.text && (
            <div className="mb-hinweis" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <b>Kontoabgleich:</b> <span>{vorschlag.text}</span>
              {vorschlagOffen && (
                <button type="button" className="mb-btn"
                  title="Zahlweise (10) und ggf. Mietschulden (12) aus dem Kontoabgleich eintragen"
                  onClick={() => setWerte((w) => ({ ...w, ...Object.fromEntries(vorschlagKeys.map((k) => [k, vorschlag[k]])) }))}>
                  Vorschlag übernehmen
                </button>
              )}
              <span style={{ fontSize: 12, opacity: 0.75 }}>
                Nur ein Vorschlag: Zahlungen vom Jobcenter oder von einem anderen Konto erkennt der Abgleich nicht — bitte prüfen.
              </span>
            </div>
          )}
          {sektionen.map((sek) => (
            <div key={sek.titel} className="mb-sektion">
              <div className="mb-sektion-titel">{sek.titel}</div>
              <div className="mb-felder">{sek.keys.map(renderFeld)}</div>
            </div>
          ))}
          <div className="mb-buttons">
            <button className="mb-btn" disabled={busy} onClick={speichern}>Zusatzangaben speichern</button>
            <button className="mb-btn primary" disabled={busy} onClick={pdfErzeugen}>
              {busy ? "Erzeugt…" : "PDF erzeugen"}
            </button>
            {!istSepa && (
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, marginLeft: 8 }}
                title="Legt das PDF zusätzlich im Vertragsordner in Dropbox ab und protokolliert es beim Vertrag">
                <input type="checkbox" checked={ablegen} onChange={(e) => setAblegen(e.target.checked)} /> in Dropbox ablegen
              </label>
            )}
          </div>
        </>
      )}
    </div>
  );
}
